import { useState } from 'react';
import { APITypes } from '../../primary-record-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getMedicalProfileChatKey,
    getChatsListCacheKey,
    onChatCrud,
    onChatMessageSent,
    onRefreshChat,
} from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import useQueryCallbacks from '../useQueryCallbacks';
import _ from 'lodash';
import { getIntl } from '../../utils/intl';
import useNotify from '../useNotify';

const intl = getIntl('useChat', {
    createChatError: 'Couldn’t start the chat. Please try again.',
    createChatMessageError: 'Couldn’t send the message. Please try again.',
    errorDeletingChat: 'Error deleting chat',
    chatStillProcessing: 'Please wait for the current response to complete.',
    chatFailed: 'This conversation has failed. Please start a new chat.',
});

export type CreateChatArgs = {
    initialPrompt: string;
};

export type UseChatReturnValue = {
    chat: APITypes.chatService.MedicalProfileChat | null;
    chats: APITypes.chatService.ListMedicalProfileChatsResponse | null;
    isLoadingChat: boolean;
    isLoadingChats: boolean;
    createChat: (args: CreateChatArgs) => void;
    isCreatingChat: boolean;
    createChatMessage: (message: string) => void;
    isWaitingOnChatMessage: boolean;
    deleteChat: (chatId: string) => void;
    isDeletingChat: boolean;
    isChatFailed: boolean;
    onChatNotFound?: () => void;
};

// Helper to check if the last model message is processing
const isLastModelMessageProcessing = (chat: APITypes.chatService.MedicalProfileChat | null | undefined): boolean => {
    if (!chat?.messages?.length) return false;
    const lastModelMessage = chat.messages.filter((m) => m.role === 'model').pop();
    return lastModelMessage?.status === 'processing';
};

// Helper to check if the last model message is failed
const isLastModelMessageFailed = (chat: APITypes.chatService.MedicalProfileChat | null | undefined): boolean => {
    if (!chat?.messages?.length) return false;
    const lastModelMessage = chat.messages.filter((m) => m.role === 'model').pop();
    return lastModelMessage?.status === 'failed';
};

export type UseChatsArgs = {
    medicalProfileId: string;
    chatId?: string | null;
    onChatIdChange?: (chatId: string | null) => void;
    defaultChatId?: boolean;
};

const isChatWithinLastDay = (chat: APITypes.chatService.MedicalProfileChatListItem) => {
    const chatDate = new Date(chat.createdDateTime);
    const now = new Date();
    return now.getTime() - chatDate.getTime() < 1000 * 60 * 60 * 24;
};

const sortChats = (chats: APITypes.chatService.ListMedicalProfileChatsResponse) => {
    return chats.items.sort((a, b) => {
        return new Date(b.createdDateTime).getTime() - new Date(a.createdDateTime).getTime();
    });
};

export const useChat = ({
    medicalProfileId,
    chatId,
    onChatIdChange,
    defaultChatId,
}: UseChatsArgs): UseChatReturnValue => {
    const notify = useNotify();
    const queryClient = useQueryClient();
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const listChatsQuery = useQuery({
        queryKey: getChatsListCacheKey(medicalProfileId),
        queryFn: async () => {
            return appAPI.get<APITypes.chatService.ListMedicalProfileChatsResponse>(
                urls.api.chats.medicalProfileChats(medicalProfileId),
            );
        },
    });

    useQueryCallbacks({
        query: listChatsQuery,
        onSuccess: (data) => {
            setIsInitialLoad(false);

            // If there is not a chat, we are done initializing; there's no chat to load
            if (!data.items.length || !defaultChatId || !!chatId || !isInitialLoad) return;

            const latestChat = sortChats(data)[0];
            if (latestChat && isChatWithinLastDay(latestChat)) {
                onChatIdChange?.(latestChat.chatId);
            }
        },
    });
    const chatQuery = useQuery({
        queryKey: getMedicalProfileChatKey(medicalProfileId, chatId || ''),
        queryFn: async () => {
            if (!chatId) {
                throw new Error('Cannot get chat without a chat id');
            }
            return appAPI.get<APITypes.chatService.GetMedicalProfileChatResponse>(
                urls.api.chats.medicalProfileChat(medicalProfileId, chatId),
            );
        },
        enabled: !!chatId && !!listChatsQuery.data?.items?.find((_) => _.chatId === chatId),
        // Poll every 2 seconds when model is processing
        refetchInterval: (query) => (isLastModelMessageProcessing(query.state.data) ? 2000 : false),
    });
    useQueryCallbacks({
        query: chatQuery,
        onSuccess: (data) => {
            if (!isLastModelMessageProcessing(data)) {
                onChatMessageSent.resetCache({ medicalProfileId })();
            }
        },
    });
    const updateChatCacheState = (
        update: (
            existingChat: APITypes.chatService.GetMedicalProfileChatResponse,
        ) => APITypes.chatService.GetMedicalProfileChatResponse,
    ) => {
        queryClient.setQueryData<APITypes.chatService.GetMedicalProfileChatResponse>(
            getMedicalProfileChatKey(medicalProfileId, chatId || ''),
            (chat) => {
                if (!chat) {
                    return chat;
                }
                return update(_.cloneDeep(chat));
            },
        );
    };
    const createChatCacheState = (chat: APITypes.chatService.GetMedicalProfileChatResponse) => {
        queryClient.setQueryData<APITypes.chatService.GetMedicalProfileChatResponse>(
            getMedicalProfileChatKey(medicalProfileId, chat.chatId),
            chat,
        );
    };
    const createChatMutation = useMutation({
        mutationFn: async (args: CreateChatArgs) => {
            const body: APITypes.chatService.CreateMedicalProfileChatRequest = {
                text: args.initialPrompt,
            };
            return appAPI.post<APITypes.chatService.CreateMedicalProfileChatAsyncResponse>(
                urls.api.chats.medicalProfileChatsAsync(medicalProfileId),
                {
                    body,
                    retryStrategy: {
                        strategy: 'no-retry',
                    },
                },
            );
        },
        retry: false,
        onSuccess: (data) => {
            createChatCacheState(data);
            onChatIdChange?.(data.chatId);
            onChatCrud.resetCache({ medicalProfileId })();
        },
        onError: (error) => {
            console.error(error);
            notify('error', intl('createChatError'));
            onChatCrud.resetCache({ medicalProfileId })();
        },
    });
    const messageMutation = useMutation({
        mutationFn: async (message: string) => {
            // Optimistic update - add user message AND processing placeholder
            const userMessageTime = Date.now();
            updateChatCacheState((chat) => {
                chat.messages.push({
                    messageDateTime: userMessageTime,
                    role: 'user',
                    text: message,
                });
                chat.messages.push({
                    messageDateTime: userMessageTime + 1,
                    role: 'model',
                    status: 'processing',
                });
                return chat;
            });
            if (!chatId) {
                throw new Error('Cannot create chat message without a chat');
            }
            const body: APITypes.chatService.CreateMedicalProfileChatMessageRequest = {
                text: message,
            };
            return appAPI.post<APITypes.chatService.CreateMedicalProfileChatMessageAsyncResponse>(
                urls.api.chats.medicalProfileChatMessagesAsync(medicalProfileId, chatId),
                {
                    body,
                    retryStrategy: {
                        strategy: 'no-retry',
                    },
                },
            );
        },
        onSuccess: (data) => {
            updateChatCacheState(() => data);
        },
        retry: false,
        onError: (error: any) => {
            console.error(error);
            // Handle specific error codes
            if (error?.response?.status === 409) {
                notify('error', intl('chatStillProcessing'));
            } else if (error?.response?.status === 400) {
                notify('error', intl('chatFailed'));
            } else {
                notify('error', intl('createChatMessageError'));
            }
            chatId && onRefreshChat.resetCache({ medicalProfileId, chatId })();
        },
    });
    const deleteChatMutation = useMutation({
        mutationFn: async (deleteChatId: string) => {
            return appAPI.del(urls.api.chats.medicalProfileChat(medicalProfileId, deleteChatId));
        },
        onSuccess: (_resp, deleteChatId) => {
            if (chatId === deleteChatId) {
                onChatIdChange?.(null);
            }
            onChatCrud.resetCache({ medicalProfileId })();
        },
        onError: (error) => {
            console.error(error);
            notify('error', intl('errorDeletingChat'));
            chatId && onRefreshChat.resetCache({ medicalProfileId, chatId })();
        },
    });

    return {
        chat: chatQuery.data || null,
        chats: listChatsQuery.data || null,
        isLoadingChats: listChatsQuery.isLoading,
        createChat: createChatMutation.mutate,
        isLoadingChat: chatQuery.isLoading,
        isCreatingChat: createChatMutation.isPending,
        createChatMessage: messageMutation.mutate,
        isWaitingOnChatMessage: messageMutation.isPending || isLastModelMessageProcessing(chatQuery.data),
        deleteChat: deleteChatMutation.mutate,
        isDeletingChat: deleteChatMutation.isPending,
        isChatFailed: isLastModelMessageFailed(chatQuery.data),
    };
};
